import api from '@/store/api'

// Parsley responses are screen state, not shared objects, so they stay out of
// the Vuex object map. A 403 here means the merchant does not have the
// capability; the default handling would send them to the login page. A
// Parsley request is not a save of the page, so it stays out of the saving
// indicator.
const READ = { receive: false, redirectOnForbidden: false, save: false }

// With receive: false a DELETE never reaches the code that removes the object
// the response names from the local store, so delete: false has no effect
// today. It is kept as a second guard in case receive is ever turned on.
const WRITE = { ...READ, delete: false }

const isPresent = value => value !== null && value !== undefined && value !== ''

const query = (params = {}) => {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (isPresent(value)) search.append(key, value)
  })
  const text = search.toString()
  return text ? `?${text}` : ''
}

const record = response => response.data.data
const payload = response => response.data

const read = endpoint => api.call('GET', endpoint, {}, READ)
const write = (method, endpoint, body = {}) => api.call(method, endpoint, body, WRITE)

// An error response with an empty body (Rails answers 404 and 403 that way)
// arrives as a plain Error whose response.data is {}. A non-empty body that is
// not JSON arrives as a SyntaxError with no response. Both get the fallback.
export const errorMessage = (error, fallback) => error?.response?.data?.errors?.[0] ?? fallback

export const parsleyApi = {
  connection: () => read('parsley/connection').then(record),
  connect: apiKey => write('POST', 'parsley/connection', { api_key: apiKey }).then(record),
  disconnect: () => write('DELETE', 'parsley/connection').then(record),
  setNutritionPanel: on => write('PUT', 'parsley/connection', { attach_nutrition_panel: on }).then(record),

  runs: () => read('parsley/sync_runs').then(record),
  run: id => read(`parsley/sync_runs/${id}`).then(record),
  startSync: () => write('POST', 'parsley/sync_runs', { kind: 'sync' }).then(record),
  startCreate: menuItemIds =>
    write('POST', 'parsley/sync_runs', { kind: 'create_products', parsley_menu_item_ids: menuItemIds }).then(record),

  menuItems: params => read(`parsley/menu_items${query(params)}`).then(payload),
  productMatches: params => read(`parsley/product_matches${query(params)}`).then(payload),

  link: (productId, menuItemId) =>
    write('POST', 'parsley/product_links', { product_id: productId, parsley_menu_item_id: menuItemId }).then(record),
  relink: (linkId, menuItemId) =>
    write('PUT', `parsley/product_links/${linkId}`, { parsley_menu_item_id: menuItemId }).then(record),
  unlink: linkId => write('DELETE', `parsley/product_links/${linkId}`).then(record)
}
